/* eslint-disable max-lines */
import React, { forwardRef } from 'react';

import PropTypes from 'prop-types';
import type { StyledComponentProps } from 'styled-components';
import styled, { css } from 'styled-components';
import type { FontSizeProps, ThemeValue } from 'styled-system';
import { fontSize } from 'styled-system';
import type { LiteralUnion } from 'type-fest';

import { BorderBox } from './BorderBox';
import { Box } from './Box';
import type { BorderProps, CommonProps, LayoutProps } from './constants';
import { COMMON, LAYOUT } from './constants';
import { IconCheck } from './Icons/Check';
import { Absolute } from './Position';
import type { Theme } from './theme';
import theme from './theme';

type BaseInputProps = {
  valid?: boolean;
  error?: string | React.ReactNode;
};

type ResizableProps = {
  resize?: 'none' | 'both' | 'horizontal' | 'vertical' | 'initial';
};

const inputStyles = css`
  display: block;
  width: 100%;
  border: none;
  border-radius: ${theme.radii[1]};
  font-size: 1rem;
  line-height: normal;
  padding: 10px;
  color: ${theme.colors.black};
  -webkit-appearance: none;

  &:focus,
  &.input-focused {
    border-color: ${theme.colors.gray.dark};
    outline: none;
  }

  textarea& {
    resize: ${({ resize }: ResizableProps) => resize};
  }

  ::placeholder {
    color: ${theme.colors.gray.medium};
  }
`;

const disabledInputStyles = css`
  background: ${theme.colors.karl.theFog};
  border-color: ${theme.colors.gray.light};
  color: ${theme.colors.gray.medium};
`;

type StyledInputProps = BaseInputProps;

const StyledInput = styled.input<StyledInputProps>`
  ${inputStyles};

  &[disabled] {
    ${disabledInputStyles}
  }

  padding-right: ${({ valid }: StyledInputProps) => (valid ? '2.6em' : 0)};
`;

const InputError = styled.div`
  border-bottom-color: ${theme.colors.yellow[3]};
  border-bottom-style: solid;
  border-bottom-width: ${theme.borderWidths[1]};
  font-size: ${theme.fontSizes.s};
  margin: 0;
  color: ${theme.colors.yellow[1]};
  background: ${theme.colors.yellow[4]};
  padding: 5px 10px;
  border-radius: ${theme.radii[1]} ${theme.radii[1]} 0 0;
`;
InputError.displayName = 'InputError';

function getBorderColor(error: boolean, valid?: boolean) {
  if (error) return theme.colors.yellow[2];
  if (typeof valid !== 'undefined' && valid) return theme.colors.green[3];
  return theme.colors.gray.light;
}

type InputProps = StyledComponentProps<'input', any, StyledInputProps, never> &
  Omit<React.HTMLProps<HTMLInputElement>, 'as' | 'label'> & {
    label?: string | React.ReactNode;
    as?: 'input' | 'textarea' | React.ComponentType<any>;
  };

const InputBox = styled(BorderBox)<React.ComponentProps<typeof BorderBox>>`
  display: block;
  &:focus-within {
    border-color: ${theme.colors.gray.dark};
    outline: none;
  }
`;

const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ error, valid, label, name, ...rest }: InputProps, ref): JSX.Element => (
    <label>
      {label}
      <InputBox
        as="span"
        className="input-box"
        mt="0.3125em"
        width="100%"
        borderColor={getBorderColor(!!error, !!valid)}
      >
        {error ? (
          <InputError data-testid={`input-error-${name || 'noName'}`} role="alert">
            {error}
          </InputError>
        ) : null}
        {/* eslint-disable-next-line react/forbid-component-props */}
        <StyledInput ref={ref} name={name} valid={valid} {...rest} />
        {valid ? (
          <Absolute right=".75em" top=".75em">
            <IconCheck color="green.3" />
          </Absolute>
        ) : null}
      </InputBox>
    </label>
  ),
);

Input.defaultProps = {
  theme,
  error: undefined,
  label: undefined,
  valid: false,
};

Input.propTypes = {
  theme: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  error: PropTypes.node,
  label: PropTypes.node,
  valid: PropTypes.bool,
};

type TextAreaProps = InputProps & ResizableProps & React.HTMLProps<HTMLInputElement>;

const TextArea = forwardRef<HTMLInputElement, TextAreaProps>(
  (props: TextAreaProps, ref): JSX.Element => <Input as="textarea" ref={ref} {...props} />,
);

TextArea.defaultProps = {
  resize: 'vertical',
};

type InputGroupProps = {
  valid?: boolean;
  invalid?: boolean;
};

const InputGroup = styled(Box)<InputGroupProps>`
  .input-box {
    border-radius: 0;
    margin-bottom: -1px;
    margin-top: 0;

    &:focus-within {
      z-index: ${theme.zIndices.pullForward};
    }
  }

  label:first-of-type .input-box {
    border-radius: ${theme.radii[1]} ${theme.radii[1]} 0 0;
  }

  label:last-of-type .input-box {
    border-radius: 0 0 ${theme.radii[1]} ${theme.radii[1]};
    margin-bottom: 0;
  }
`;

InputGroup.defaultProps = { theme };

InputGroup.propTypes = {
  ...LAYOUT.propTypes,
  ...COMMON.propTypes,
  theme: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  valid: PropTypes.bool,
  invalid: PropTypes.bool,
};

type StyledSelectProps = CommonProps &
  LayoutProps &
  FontSizeProps<Theme, LiteralUnion<ThemeValue<'fontSizes', Theme>, string | number>> &
  React.HTMLProps<HTMLSelectElement>;

const StyledSelect = styled.select<SelectProps>`
  ${inputStyles}
  height: 2.5rem;
  font-size: ${theme.fontSizes.m};
  padding: 0 10px;
  border: 0;
  -webkit-appearance: menulist;

  &:focus,
  &.input-focused {
    border-color: ${theme.colors.gray.dark};
    outline: none;
  }

  ${COMMON}
  ${LAYOUT}
  ${fontSize}
`;

StyledSelect.defaultProps = {
  theme,
};

StyledSelect.propTypes = {
  ...(fontSize as any).propTypes,
  theme: PropTypes.object, // eslint-disable-line react/forbid-prop-types
};

type SelectProps = StyledComponentProps<'select', any, StyledSelectProps, never> &
  React.HTMLProps<HTMLSelectElement> & {
    label?: string | React.ReactNode;
    error?: string | React.ReactNode;
    as?: 'select';
  };

function Select({ error, label, children, ...rest }: SelectProps): JSX.Element {
  return (
    <label>
      {label}
      <InputBox as="span" className="input-box" mt="0.3125em" width="100%" borderColor={getBorderColor(!!error)}>
        {error ? <InputError role="alert">{error}</InputError> : null}
        <StyledSelect {...rest}>{children}</StyledSelect>
      </InputBox>
    </label>
  );
}

Select.defaultProps = {
  ...StyledSelect.defaultProps,
  theme,
  error: undefined,
  label: undefined,
};

Select.propTypes = {
  ...StyledSelect.propTypes,
  theme: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  error: PropTypes.node,
  label: PropTypes.node,
};

const StyledBooleanInput = styled.input`
  float: left;
  margin-right: 5px;
  margin-top: 5px;
  margin-left: -20px;
  font-size: inherit;
  line-height: normal;

  &[disabled] {
    ${disabledInputStyles}
  }
`;

type BooleanInputLabelProps = {
  disabled?: boolean;
};

const BooleanInputLabel = styled.label<BooleanInputLabelProps>`
  display: inline;
  cursor: ${({ disabled }: BooleanInputLabelProps) => (disabled ? 'default' : 'pointer')};
`;

// eslint-disable-next-line @typescript-eslint/ban-types
type BooleanInputProps = StyledComponentProps<'input', any, {}, never> &
  BooleanInputLabelProps &
  Omit<React.HTMLProps<HTMLInputElement>, 'ref' | 'as'> & {
    label: string | React.ReactNode;
    value?: string;
    checked?: boolean;
    type: 'checkbox' | 'radio';
  };

function BooleanInput({ disabled, label, type, ...rest }: BooleanInputProps): JSX.Element {
  return (
    <Box pl="m">
      <BooleanInputLabel disabled={disabled}>
        <StyledBooleanInput type={type} disabled={disabled} {...rest} />
        {label}
      </BooleanInputLabel>
    </Box>
  );
}

BooleanInput.defaultProps = {
  theme,
  disabled: false,
};

BooleanInput.propTypes = {
  theme: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  disabled: PropTypes.bool,
  label: PropTypes.node.isRequired,
  type: PropTypes.oneOf(['checkbox', 'radio']).isRequired,
};

function Checkbox(props: Omit<BooleanInputProps, 'type'>): JSX.Element {
  return <BooleanInput type="checkbox" {...props} />;
}
function RadioButton(props: Omit<BooleanInputProps, 'type'>): JSX.Element {
  return <BooleanInput type="radio" {...props} />;
}

type RadioButtonGroupProps = CommonProps &
  LayoutProps &
  BorderProps & {
    error?: string | React.ReactNode;
    value?: string;
  };

function RadioButtonGroup({
  children,
  error,
  color,
  ...rest
}: React.PropsWithChildren<RadioButtonGroupProps>): JSX.Element {
  return (
    <BorderBox
      borderWidth={error ? 1 : 0}
      color={color as any}
      borderColor={error ? theme.colors.yellow[2] : 'none'}
      {...rest}
    >
      {error && <InputError role="alert">{error}</InputError>}
      <Box px={error ? 'xs' : 'n'}>{children}</Box>
    </BorderBox>
  );
}

RadioButtonGroup.defaultProps = {
  theme,
  error: undefined,
};

RadioButtonGroup.propTypes = {
  theme: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  children: PropTypes.node.isRequired,
  error: PropTypes.node,
};

type FormErrorProps = CommonProps & LayoutProps & BorderProps;

const FormErrorInner = styled(InputError)`
  border-radius: 5px;
  border-bottom-style: none;
`;

function FormError({ children, color, ...rest }: React.PropsWithChildren<FormErrorProps>): JSX.Element {
  return (
    <BorderBox color={color as any} border="inherit" borderColor="yellow.3" {...rest}>
      <FormErrorInner>{children}</FormErrorInner>
    </BorderBox>
  );
}

FormError.defaultProps = {
  theme,
};

FormError.propTypes = {
  theme: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  children: PropTypes.node.isRequired,
};

Input.displayName = 'Input';
TextArea.displayName = 'TextArea';
InputGroup.displayName = 'InputGroup';
Select.displayName = 'Select';
Checkbox.displayName = 'Checkbox';
RadioButton.displayName = 'RadioButton';
RadioButtonGroup.displayName = 'RadioButtonGroup';
FormError.displayName = 'FormError';

export { Input };
export { TextArea };
export { InputGroup };
export { Select };
export { Checkbox };
export { RadioButton };
export { RadioButtonGroup };
export { FormError };
